/** @prettier */

//API import
import {Storage} from 'aws-amplify';
import {S3ProviderListOutput} from '@aws-amplify/storage';
import {TFunction} from 'i18next';

/** 言語情報の定義一覧 */
export const languageInfo = [
  'english',
  'dutch',
  'french',
  'german',
  'greek',
  'italian',
  'polish',
  'portuguese',
  'spanish',
  'turkish',
];

// 言語情報の型
type LanguageInfoType = (typeof languageInfo)[number];

// 公開ファイル一覧の型
export interface PublicFileList {
  fileNameList: Record<LanguageInfoType, string>;
  fileDateList: Record<LanguageInfoType, string>;
}

/**
 * S3上の公開バケットからファイル一覧を取得する関数
 * @param customPrefix 対象のフォルダ名
 */
export const getPublicS3Files = async (
  customPrefix: string
): Promise<S3ProviderListOutput['results']> => {
  let allResults: S3ProviderListOutput = {
    results: [],
    hasNextToken: false,
  };
  let nextToken: string | undefined = undefined;

  try {
    Storage.configure({
      customPrefix: {public: customPrefix},
    });

    do {
      const result: S3ProviderListOutput = await Storage.list('', {
        level: 'public',
        pageSize: 1000,
        nextToken,
      });

      allResults.results.push(...result.results);
      nextToken = result.nextToken || undefined;
    } while (nextToken);
  } catch (error) {
    throw new Error('Failed to fetch files from S3');
  }

  return allResults.results;
};

/**
 * ファイル一覧から表示用のデータを生成する関数
 * @param fileNameList ファイル名一覧
 * @param filePrefix　対象ファイル種類
 */
export function createDisplayDataFromFileNames(
  fileNameList: string[],
  filePrefix: string
): {
  /** 現在適応されているファイル情報 */
  current: PublicFileList;
  /** 次に適応予定のファイル情報 */
  nextScheduled: PublicFileList;
} {
  // 現在適応されているファイル情報
  const currentFiles: PublicFileList = {
    fileNameList: {},
    fileDateList: {},
  };

  // 次に適用予定のファイル情報
  const nextScheduledFiles: PublicFileList = {
    fileNameList: {},
    fileDateList: {},
  };

  //言語設定ごとにオブジェクトを生成
  languageInfo.forEach(lang => {
    // 現在のファイルリストと日付リストを初期化
    currentFiles.fileNameList[lang] = '';
    // 次に適用されるファイルリストと日付リストを初期化
    nextScheduledFiles.fileNameList[lang] = '';

    //オブジェクトが取りうる最小値、最大値を指定していない理由は日付情報は20yymmddで定義されるため。
    //20yy年に限定している根拠は特になし。キリが良い値でかつ明らかに不自然な値を弾く為
    currentFiles.fileDateList[lang] = '19991231';
    nextScheduledFiles.fileDateList[lang] = '21000101';
  });

  /**
   * ファイル名から日付情報と言語情報を抽出する関数
   * @param fileName - ファイル名
   * @param filePrefix - ファイルの開始文字
   * @returns 有効な日付なら抜き出した日付情報と言語情報を返す
   */
  const parseFileInfo = (
    fileName: string | undefined,
    filePrefix: string
  ): {dateInfo: string | null; languageInfo: string | null} => {
    //ファイルが存在しない場合
    if (fileName === undefined) return {dateInfo: null, languageInfo: null};

    const languagePattern = languageInfo.join('|');

    //正規表現：プレフィックス_日付情報_言語情報.pdf
    const regex = new RegExp(
      `^${filePrefix}_(20\\d{6})_(${languagePattern})\\.pdf$`
    );

    const matchResult = fileName.match(regex);
    //フォーマットに沿うPDFが存在しない場合
    if (matchResult === null) return {dateInfo: null, languageInfo: null};

    const year = parseInt(matchResult[1].slice(0, 4), 10);
    const month = parseInt(matchResult[1].slice(4, 6), 10) - 1; // 月は0始まり
    const day = parseInt(matchResult[1].slice(6), 10);

    //日付オブジェクトを生成
    const createDate = new Date(Date.UTC(year, month, day));

    //ファイルの日付情報が有効な値か確認する
    //Dateオブジェクトは無効な日付の場合、勝手に修正（例:2月30日を3月１日）するのでので必要
    const isValidDate =
      createDate.getUTCFullYear() === year &&
      createDate.getUTCMonth() === month &&
      createDate.getUTCDate() === day;

    return isValidDate
      ? //有効な日付の場合
        {dateInfo: matchResult[1], languageInfo: matchResult[2]}
      : //無効な日付の場合
        {dateInfo: null, languageInfo: null};
  };

  /**
   * 現在のUTCの日付をyyyymmdd形式の文字列で返す関数
   * @returns {string} yyyymmdd形式のUTC日付
   */
  const formatTodayDateUtc = () => {
    const now = new Date();
    const day = String(now.getUTCDate()).padStart(2, '0');
    const month = String(now.getUTCMonth() + 1).padStart(2, '0');
    const year = now.getUTCFullYear();

    return `${year}${month}${day}`;
  };

  const todayDateUtc = formatTodayDateUtc();

  //ファイル一覧から表示対象のファイル情報を抜き出す
  fileNameList.forEach(fileName => {
    // ファイル名から日付と言語を抽出
    const {dateInfo, languageInfo: language} = parseFileInfo(
      fileName,
      filePrefix
    );

    //ルールに沿っていないファイルは処理対象外
    if (language === null || dateInfo === null) {
      return;
    }

    // 現在適応されているファイルの処理
    if (
      dateInfo <= todayDateUtc &&
      (currentFiles.fileDateList[language] === undefined ||
        dateInfo > currentFiles.fileDateList[language])
    ) {
      currentFiles.fileNameList[language] = fileName;
      currentFiles.fileDateList[language] = dateInfo;
    }

    // 次に適用予定のファイルの処理
    if (
      dateInfo > todayDateUtc &&
      (nextScheduledFiles.fileDateList[language] === undefined ||
        dateInfo < nextScheduledFiles.fileDateList[language])
    ) {
      nextScheduledFiles.fileNameList[language] = fileName;
      nextScheduledFiles.fileDateList[language] = dateInfo;
    }
  });

  return {
    current: currentFiles,
    nextScheduled: nextScheduledFiles,
  };
}

/**
 * URLに表示する日付を生成する関数
 *
 * @param {string | null} date- ファイルから抜き出した日付情報
 * @returns {string} URLに表示する日付（"DD.MMMYYYY"形式）。日付が無効な場合は空文字列を返す。
 */
export function createURLDate(date: string | null, trans: TFunction): string {
  if (date) {
    const monthType = [
      trans('helpFunction.message.month.january'),
      trans('helpFunction.message.month.february'),
      trans('helpFunction.message.month.march'),
      trans('helpFunction.message.month.april'),
      trans('helpFunction.message.month.may'),
      trans('helpFunction.message.month.june'),
      trans('helpFunction.message.month.july'),
      trans('helpFunction.message.month.august'),
      trans('helpFunction.message.month.september'),
      trans('helpFunction.message.month.october'),
      trans('helpFunction.message.month.november'),
      trans('helpFunction.message.month.december'),
    ];

    const year = parseInt(date.slice(0, 4), 10);
    const month = parseInt(date.slice(4, 6), 10) - 1;
    const day = parseInt(date.slice(6), 10);

    const monthName = monthType[month];

    return `${day}.${monthName}${year}`;
  } else {
    return '';
  }
}
/**
 * S3の公開バケットに保存されているファイルのURLを返す関数
 * @param {string} fileKey:- ファイル名
 * @returns {Promise<string|null>} - ダウンロードURL（成功時）または null（エラー時）
 */
const getPublicFileUrl = async (fileKey: string) => {
  try {
    const url = await Storage.get(fileKey, {level: 'public'});

    //ユーザーに一貫したエクスペリエンスを与えるため、MACCESSと同じドメインが表示されるように修正
    //例
    //変更前：https://s3.ap-northeast-1.amazonaws.com/store.m-access.cloud/public-document/privacy-policy_20240115.pdf
    //変更後：https://store.m-access.cloud/public-document/privacy-policy_20240115.pdf
    const cleanUrl = url.replace(/^https:\/\/[^/]+/, 'https://');

    return cleanUrl;
  } catch (error) {
    return null;
  }
};

/**
 * ファイルのダウンロードURLを取得して、新しいタブで開く関数
 * @param fileKey - ダウンロードするファイルのキー
 *  @param errorText -　エラーメッセージ
 */
export const createDownloadLink = async (
  fileKey: string,
  errorText: string
) => {
  try {
    const url = await getPublicFileUrl(fileKey);

    if (url) {
      window.open(url, '_blank');
    } else {
      throw new Error('URL not found');
    }
  } catch (error) {
    alert(errorText);
  }
};
